import {
  HP_DISPLAY_MAX,
  MAX_HP,
  UNIT_KINDS,
  displayHpToInternal,
  terrainEnterableByUnit,
  tileDef,
  unitDef,
  type PlayerId,
  type TileKind,
  type UnitKind,
} from "@dailywars/core/engine";
import { useMemo } from "react";

import { CoinMoneyIcon } from "#/components/game/CoinMoneyIcon";
import { UnitIconLayers } from "#/components/game/UnitIconLayers";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "#/components/ui/select";
import { hpBadgeUrl, isHpBadgeValue } from "#/game/badgeSprites";
import { cn } from "#/lib/utils";

import { CODEX_TILE_KINDS } from "./-codex.data";
import { CodexTileLayers } from "./-codex.icons";

const HP_OPTIONS = Array.from({ length: HP_DISPLAY_MAX }, (_, index) => index + 1);

export const CODEX_ATTACKER_UNIT_KINDS = UNIT_KINDS.filter(
  (kind) => kind !== "landTransport" && kind !== "transportCopter",
);

function allowedTerrainsForUnit(kind: UnitKind): TileKind[] {
  return CODEX_TILE_KINDS.filter((tileKind) =>
    terrainEnterableByUnit({ unitKind: kind, tileKind }),
  );
}

export function pickTerrainForUnit({
  kind,
  current,
}: {
  kind: UnitKind;
  current: TileKind;
}): TileKind {
  const allowed = allowedTerrainsForUnit(kind);
  return allowed.includes(current) ? current : allowed[0]!;
}

function CodexCalculatorUnitCost({ kind, displayHp }: { kind: UnitKind; displayHp: number }) {
  const scaledCost = (displayHpToInternal(displayHp) / MAX_HP) * unitDef(kind).cost;

  return (
    <span className="flex items-center gap-0.5 text-sm tabular-nums text-muted-foreground">
      {Math.round(scaledCost).toLocaleString()}
      <CoinMoneyIcon size="sm" className="!h-3 !w-3" />
    </span>
  );
}

function CodexCalculatorPortrait({
  kind,
  terrain,
  player,
  displayHp,
}: {
  kind: UnitKind;
  terrain: TileKind;
  player: PlayerId;
  displayHp: number;
}) {
  const unitName = unitDef(kind).name;

  return (
    <div
      className="relative size-16 shrink-0 overflow-hidden rounded-sm border border-border"
      aria-hidden
    >
      <CodexTileLayers kind={terrain} player={player} />
      <UnitIconLayers
        kind={kind}
        player={player}
        className={cn("absolute inset-0 z-10 m-auto size-14", player === "p2" && "-scale-x-100")}
      />
      {isHpBadgeValue(displayHp) ? (
        <img
          src={hpBadgeUrl(displayHp)}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 size-full [image-rendering:pixelated]"
        />
      ) : null}
      <span className="sr-only">
        {unitName} on {tileDef(terrain).name} at {displayHp} HP
      </span>
    </div>
  );
}

function CodexCalculatorUnitSelect({
  kind,
  unitKinds,
  onKindChange,
}: {
  kind: UnitKind;
  unitKinds: readonly UnitKind[];
  onKindChange: (kind: UnitKind) => void;
}) {
  return (
    <Select value={kind} onValueChange={(value) => value && onKindChange(value as UnitKind)}>
      <SelectTrigger className="w-full min-w-0" size="sm">
        <SelectValue className="truncate">{unitDef(kind).name}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {unitKinds.map((unitKind) => (
            <SelectItem key={unitKind} value={unitKind}>
              {unitDef(unitKind).name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function CodexCalculatorTerrainSelect({
  unitKind,
  terrain,
  onTerrainChange,
}: {
  unitKind: UnitKind;
  terrain: TileKind;
  onTerrainChange: (terrain: TileKind) => void;
}) {
  const allowedTerrains = useMemo(() => allowedTerrainsForUnit(unitKind), [unitKind]);
  const value = allowedTerrains.includes(terrain) ? terrain : allowedTerrains[0]!;

  return (
    <Select value={value} onValueChange={(next) => next && onTerrainChange(next as TileKind)}>
      <SelectTrigger className="w-full" size="sm">
        <SelectValue>{tileDef(value).name}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {allowedTerrains.map((tileKind) => (
            <SelectItem key={tileKind} value={tileKind}>
              {tileDef(tileKind).name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function CodexCalculatorHpSelect({
  displayHp,
  onDisplayHpChange,
}: {
  displayHp: number;
  onDisplayHpChange: (displayHp: number) => void;
}) {
  return (
    <Select
      value={String(displayHp)}
      onValueChange={(value) => value && onDisplayHpChange(Number(value))}
    >
      <SelectTrigger className="w-full" size="sm">
        <SelectValue>{displayHp} HP</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {HP_OPTIONS.map((hp) => (
            <SelectItem key={hp} value={String(hp)}>
              {hp} HP
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export type CodexCalculatorColumnProps = {
  player: PlayerId;
  unitKind: UnitKind;
  unitKinds: readonly UnitKind[];
  terrain: TileKind;
  displayHp: number;
  onUnitKindChange: (kind: UnitKind) => void;
  onTerrainChange: (terrain: TileKind) => void;
  onDisplayHpChange: (displayHp: number) => void;
};

export function CodexCalculatorPortraitBlock({
  player,
  unitKind,
  terrain,
  displayHp,
}: Pick<CodexCalculatorColumnProps, "player" | "unitKind" | "terrain" | "displayHp">) {
  return (
    <div className="flex shrink-0 flex-col items-center gap-1.5">
      <CodexCalculatorPortrait
        kind={unitKind}
        terrain={terrain}
        player={player}
        displayHp={displayHp}
      />
      <CodexCalculatorUnitCost kind={unitKind} displayHp={displayHp} />
    </div>
  );
}

export function CodexCalculatorControls({
  unitKind,
  unitKinds,
  terrain,
  displayHp,
  onUnitKindChange,
  onTerrainChange,
  onDisplayHpChange,
}: Omit<CodexCalculatorColumnProps, "player">) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <CodexCalculatorUnitSelect
        kind={unitKind}
        unitKinds={unitKinds}
        onKindChange={onUnitKindChange}
      />
      <CodexCalculatorTerrainSelect
        unitKind={unitKind}
        terrain={terrain}
        onTerrainChange={onTerrainChange}
      />
      <CodexCalculatorHpSelect displayHp={displayHp} onDisplayHpChange={onDisplayHpChange} />
    </div>
  );
}

export function willKillFromCounter({
  counterDamage,
  attackerDisplayHp,
}: {
  counterDamage: number | null;
  attackerDisplayHp: number;
}): boolean {
  return counterDamage !== null && counterDamage >= displayHpToInternal(attackerDisplayHp);
}
